/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronRight, 
  Check, 
  User, 
  FileText, 
  Sliders, 
  Filter, 
  ExternalLink,
  Settings as SettingsIcon,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  Upload,
  AlertCircle,
  CheckCircle2,
  Lock,
  Search,
  Zap,
  Layout,
  Target,
  ShieldCheck,
  Cpu,
  RefreshCw,
  Rocket,
  Type // Fixed: Added missing import
} from 'lucide-react';
import { getFormControlConfigs, setFormControlConfigs, checkExtensionStatus } from '../utils/extensionSync';
import axiosInstance from '../utils/axiosInstance';

// --- Sub-components moved to top to ensure proper initialization ---

const PersonalInfoStep = ({ personalInfo, setPersonalInfo }) => (
  <div className="space-y-6 sm:space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
    <div>
      <h3 className="text-xl sm:text-2xl font-black text-slate-900 italic tracking-tight">Personal Information</h3>
      <p className="text-slate-500 text-[10px] sm:text-xs font-medium italic mt-1">This info is used to pre-fill application forms.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
      <div className="space-y-2">
        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">First Name</label>
        <input className="w-full px-4 py-3 sm:px-5 sm:py-4 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl text-sm sm:text-base md:text-sm font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner italic" value={personalInfo.firstName} onChange={(e) => setPersonalInfo({ ...personalInfo, firstName: e.target.value })} placeholder="John" />
      </div>
      <div className="space-y-2">
        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Last Name</label>
        <input className="w-full px-4 py-3 sm:px-5 sm:py-4 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl text-sm sm:text-base md:text-sm font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner italic" value={personalInfo.lastName} onChange={(e) => setPersonalInfo({ ...personalInfo, lastName: e.target.value })} placeholder="Doe" />
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
      <div className="space-y-2">
        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Email</label>
        <input className="w-full px-4 py-3 sm:px-5 sm:py-4 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl text-sm sm:text-base md:text-sm font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner italic" type="email" value={personalInfo.email} onChange={(e) => setPersonalInfo({ ...personalInfo, email: e.target.value })} placeholder="john@domain.com" />
      </div>
      <div className="space-y-2">
        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Phone</label>
        <input className="w-full px-4 py-3 sm:px-5 sm:py-4 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl text-sm sm:text-base md:text-sm font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner italic" value={personalInfo.phone} onChange={(e) => setPersonalInfo({ ...personalInfo, phone: e.target.value })} placeholder="+1..." />
      </div>
    </div>
    <div className="space-y-2">
      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Location</label>
      <input className="w-full px-4 py-3 sm:px-5 sm:py-4 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl text-sm sm:text-base md:text-sm font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner italic" value={personalInfo.location} onChange={(e) => setPersonalInfo({ ...personalInfo, location: e.target.value })} placeholder="City, Country" />
    </div>
  </div>
);

const ResumeStep = ({ resumeFile, setResumeFile, resumeTitle, setResumeTitle, resumes, loadingResumes, handleActivateResume }) => {
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) { setResumeFile(file); if (!resumeTitle) setResumeTitle(file.name.replace(/\.[^/.]+$/, '').toUpperCase()); }
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl sm:text-2xl font-black text-slate-900 italic tracking-tight">Resume Management</h3>
          <p className="text-slate-500 text-[10px] sm:text-xs font-medium italic mt-1">Select an existing resume or upload a new one.</p>
        </div>
      </div>

      {resumes.length > 0 && (
         <div className="space-y-4">
            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Existing Assets</label>
            <div className="grid grid-cols-1 gap-3">
               {resumes.map((r) => (
                  <div key={r.id} className={`flex flex-col sm:flex-row items-center justify-between p-5 rounded-[2rem] border-2 transition-all group relative overflow-hidden ${r.is_active ? 'bg-emerald-50/30 border-emerald-400 shadow-lg shadow-emerald-500/5' : 'bg-white border-slate-100 hover:border-blue-400'}`}>
                     <div className="flex-1 flex items-center gap-4 min-w-0 w-full">
                        <div className={`w-12 h-12 rounded-[1.2rem] flex items-center justify-center transition-all duration-500 shrink-0 ${r.is_active ? 'bg-emerald-500 text-white' : 'bg-slate-50 text-slate-300 group-hover:bg-blue-600 group-hover:text-white'}`}>
                           <FileText size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                           <div className="flex items-center gap-2 mb-0.5 min-w-0 flex-wrap">
                              <span className="text-xs font-black text-slate-900 italic tracking-tight uppercase truncate max-w-full">{r.title || r.filename}</span>
                              {r.is_active && (
                                 <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500 text-white text-[7px] font-black uppercase rounded-full shadow-lg shadow-emerald-200 italic tracking-widest shrink-0">
                                    <ShieldCheck size={8}/> Primary
                                 </div>
                              )}
                           </div>
                           <div className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] italic">{new Date(r.created_at).toLocaleDateString()}</div>
                        </div>
                     </div>
                     {!r.is_active && (
                        <button onClick={() => { console.log("Activating resume:", r.id); handleActivateResume(r.id); }} className="mt-4 sm:mt-0 px-5 py-2.5 bg-blue-600 text-white rounded-[1.2rem] text-[8px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-xl active:scale-95 italic whitespace-nowrap shrink-0">Set Primary</button>
                     )}
                  </div>
               ))}
            </div>
         </div>
      )}

      <div className="space-y-6">
        <div className="relative pt-6 border-t border-slate-100">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 bg-white text-[8px] font-black text-slate-400 uppercase tracking-[0.3em]">Or Upload New</div>
        </div>
        
        <div className="space-y-2">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Resume Label</label>
          <input className="w-full px-4 py-3 sm:px-5 sm:py-4 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl text-sm sm:text-[10px] font-black uppercase tracking-widest outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-500 transition-all border-b-4 shadow-inner italic" value={resumeTitle} onChange={(e) => setResumeTitle(e.target.value)} placeholder="E.G. SOFTWARE ENGINEER" />
        </div>
        <div className="relative group">
           <input type="file" accept=".pdf,.doc,.docx" onChange={handleFile} className="hidden" id="wizard-resume" />
           <label htmlFor="wizard-resume" className="flex flex-col items-center justify-center p-6 sm:p-8 md:p-10 border-4 border-dashed border-slate-100 rounded-[1.5rem] sm:rounded-[2rem] bg-slate-50/50 hover:bg-white hover:border-blue-400 hover:shadow-2xl hover:shadow-blue-500/5 transition-all cursor-pointer group">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white border border-slate-200 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform mb-3 sm:mb-4">
                 <Upload className="text-blue-500 sm:w-6 sm:h-6" size={20} />
              </div>
              <span className="text-[9px] sm:text-[10px] font-black text-slate-800 uppercase tracking-widest italic text-center px-2 break-words max-w-full">{resumeFile ? resumeFile.name : 'Select or Drop File'}</span>
           </label>
        </div>
      </div>
    </div>
  );
};

const FormControlStep = ({
  inputFieldConfigs, setInputFieldConfigs,
  radioButtons, setRadioButtons,
  dropdowns, setDropdowns,
  newInputPlaceholder, setNewInputPlaceholder,
  newInputValue, setNewInputValue,
  newRadioPlaceholder, setNewRadioPlaceholder,
  newRadioValue, setNewRadioValue,
  newDropdownPlaceholder, setNewDropdownPlaceholder,
  newDropdownValue, setNewDropdownValue
}) => {
  const add = (list, setList, key, val, setKey, setVal, label) => {
    if (!key.trim() || !val.trim()) return;
    if (list.some(i => i.placeholderIncludes === key.trim())) return;
    setList([...list, { placeholderIncludes: key.trim(), [label === 'Dropdown' ? 'value' : 'defaultValue']: val.trim(), createdAt: Date.now() }]);
    setKey(''); setVal('');
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
      <div>
         <h3 className="text-xl sm:text-2xl font-black text-slate-900 italic tracking-tight">Custom Automations</h3>
         <p className="text-slate-500 text-[10px] sm:text-xs font-medium italic mt-1">Define specific rules for filling out application forms.</p>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {[
          { label: 'Text Input Logic', icon: Type, list: inputFieldConfigs, set: setInputFieldConfigs, k: newInputPlaceholder, sk: setNewInputPlaceholder, v: newInputValue, sv: setNewInputValue, ph: 'Label Tag' },
          { label: 'Radio Button Logic', icon: CheckCircle2, list: radioButtons, set: setRadioButtons, k: newRadioPlaceholder, sk: setNewRadioPlaceholder, v: newRadioValue, sv: setNewRadioValue, ph: 'Question Trigger' }
        ].map((sec, i) => (
          <div key={i} className="p-4 sm:p-6 md:p-8 border border-slate-200 rounded-[2rem] sm:rounded-[2.5rem] bg-white group hover:border-blue-300 transition-all">
            <h4 className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4 sm:mb-6 flex items-center gap-2 italic">
               <sec.icon size={14} className="sm:w-4 sm:h-4 text-blue-500 flex-shrink-0" /> <span className="truncate">{sec.label}</span>
            </h4>
            <div className="flex flex-col md:flex-row gap-2 sm:gap-3 mb-4 sm:mb-6">
              <input className="flex-1 px-4 py-3 sm:px-5 sm:py-3.5 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl text-sm sm:text-base md:text-[10px] font-black outline-none focus:bg-white focus:border-blue-400 transition-all italic shadow-inner" placeholder={sec.ph} value={sec.k} onChange={(e) => sec.sk(e.target.value)} />
              <input className="flex-1 px-4 py-3 sm:px-5 sm:py-3.5 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl text-sm sm:text-base md:text-[10px] font-black outline-none focus:bg-white focus:border-blue-400 transition-all italic shadow-inner" placeholder="Value to Fill" value={sec.v} onChange={(e) => sec.sv(e.target.value)} />
              <button className="px-4 py-3 sm:px-6 sm:py-3.5 bg-slate-900 text-white rounded-xl sm:rounded-2xl hover:bg-black transition-all shadow-xl active:scale-95 flex-shrink-0 flex items-center justify-center" onClick={() => add(sec.list, sec.set, sec.k, sec.v, sec.sk, sec.sv, sec.label)}><Plus size={18} className="sm:w-5 sm:h-5"/></button>
            </div>
            {sec.list.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {sec.list.map((item, idx) => (
                   <div key={idx} className="flex items-center gap-3 bg-blue-50 border border-blue-100 pl-4 pr-2 py-2 rounded-xl group/item">
                      <span className="text-[10px] font-black text-blue-700 italic uppercase truncate max-w-[120px]">{item.placeholderIncludes}</span>
                      <div className="text-[10px] font-black text-blue-300">➔</div>
                      <span className="text-[10px] font-bold text-blue-600">{item.defaultValue || item.value}</span>
                      <button onClick={() => sec.set(sec.list.filter((_, i) => i !== idx))} className="ml-1 p-1 hover:bg-white rounded-full transition-colors text-blue-400 hover:text-rose-500"><X size={12}/></button>
                   </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const FiltersStep = ({
  titleSkipEnabled, setTitleSkipEnabled,
  titleFilterEnabled, setTitleFilterEnabled,
  badWordsEnabled, setBadWordsEnabled,
  titleSkipWords, setTitleSkipWords,
  titleFilterWords, setTitleFilterWords,
  badWords, setBadWords,
  newTitleSkip, setNewTitleSkip,
  newTitleFilter, setNewTitleFilter,
  newBadWord, setNewBadWord
}) => {
  const add = (word, list, setList, setIn) => {
    const t = word.trim(); if (!t) return;
    if (list.includes(t)) return;
    setList([...list, t]); setIn('');
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
      <div>
        <h3 className="text-xl sm:text-2xl font-black text-slate-900 italic tracking-tight">Job Filters</h3>
        <p className="text-slate-500 text-[10px] sm:text-xs font-medium italic mt-1">Configure which jobs to target or avoid.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        {[
          { id: 'sk', label: 'Title Skips', sub: 'Exclude jobs with these words', color: 'rose', val: titleSkipWords, set: setTitleSkipWords, en: titleSkipEnabled, setEn: setTitleSkipEnabled, n: newTitleSkip, sn: setNewTitleSkip },
          { id: 'fi', label: 'Title Must Include', sub: 'Only apply if title contains', color: 'blue', val: titleFilterWords, set: setTitleFilterWords, en: titleFilterEnabled, setEn: setTitleFilterEnabled, n: newTitleFilter, sn: setNewTitleFilter },
          { id: 'ba', label: 'Description Bad Words', sub: 'Skip keyphrases in description', color: 'amber', val: badWords, set: setBadWords, en: badWordsEnabled, setEn: setBadWordsEnabled, n: newBadWord, sn: setNewBadWord }
        ].map((sec) => (
           <div key={sec.id} className={`p-4 sm:p-6 md:p-8 rounded-[2rem] sm:rounded-[2.5rem] border-2 transition-all duration-500 ${sec.en ? `bg-white border-${sec.color}-200 shadow-xl shadow-${sec.color}-500/5` : 'bg-slate-50 border-slate-100 opacity-60'}`}>
              <div className="flex items-center justify-between mb-6 sm:mb-8 gap-3">
                 <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                    <div className={`p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-${sec.color}-100 text-${sec.color}-600 shadow-inner flex-shrink-0`}><Target size={18} className="sm:w-5 sm:h-5"/></div>
                    <div className="min-w-0 flex-1">
                       <h4 className="text-[10px] sm:text-xs font-black text-slate-900 uppercase tracking-widest italic truncate">{sec.label}</h4>
                       <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-tighter mt-0.5 line-clamp-1">{sec.sub}</p>
                    </div>
                 </div>
                 <label className="relative inline-flex items-center cursor-pointer scale-100 sm:scale-110 flex-shrink-0">
                    <input type="checkbox" className="sr-only peer" checked={sec.en} onChange={() => sec.setEn(!sec.en)} />
                    <div className={`w-11 h-5.5 sm:w-12 sm:h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] sm:after:top-[4px] after:left-[2px] sm:after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3.5 sm:after:h-4 after:w-3.5 sm:after:w-4 after:transition-all peer-checked:bg-${sec.color}-500 shadow-inner`}></div>
                 </label>
              </div>

              {sec.en && (
                 <div className="space-y-4 sm:space-y-6 animate-in slide-in-from-top-4">
                    <div className="flex gap-2 sm:gap-3">
                       <input className="flex-1 px-4 py-3 sm:px-5 sm:py-3.5 bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl text-sm sm:text-base md:text-[10px] font-black outline-none focus:bg-white focus:border-blue-400 transition-all italic shadow-inner" placeholder="Inject Keyword..." value={sec.n} onChange={(e) => sec.sn(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add(sec.n, sec.val, sec.set, sec.sn)} />
                       <button className={`px-4 py-3 sm:px-6 sm:py-3.5 bg-${sec.color}-500 text-white rounded-xl sm:rounded-2xl hover:bg-${sec.color}-600 transition-all shadow-lg active:scale-95 flex-shrink-0`} onClick={() => add(sec.n, sec.val, sec.set, sec.sn)}><Plus size={18} className="sm:w-5 sm:h-5"/></button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                       {sec.val.map(w => (
                          <div key={w} className={`flex items-center gap-2 sm:gap-3 bg-${sec.color}-50 border border-${sec.color}-100 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl`}>
                             <span className="text-[9px] sm:text-[10px] font-black text-slate-700 italic uppercase break-words max-w-[200px] sm:max-w-none">{w}</span>
                             <button onClick={() => sec.set(sec.val.filter(x => x !== w))} className="p-0.5 sm:p-1 text-slate-400 hover:text-rose-500 transition-colors flex-shrink-0"><X size={10} className="sm:w-3 sm:h-3"/></button>
                          </div>
                       ))}
                    </div>
                 </div>
              )}
           </div>
        ))}
      </div>
    </div>
  );
};

const CompleteStep = ({ extensionStatus }) => (
  <div className="flex flex-col items-center justify-center py-10 sm:py-16 md:py-20 text-center animate-in zoom-in duration-700">
    <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 bg-emerald-100 text-emerald-600 rounded-[2rem] sm:rounded-[2.5rem] md:rounded-[3rem] flex items-center justify-center mb-6 sm:mb-8 md:mb-10 shadow-2xl shadow-emerald-500/20 relative group">
       <div className="absolute inset-0 bg-emerald-500 rounded-[2rem] sm:rounded-[2.5rem] md:rounded-[3rem] animate-ping opacity-20 group-hover:opacity-40" />
       <ShieldCheck size={48} className="sm:w-14 sm:h-14 md:w-16 md:h-16 relative z-10" />
    </div>
    <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 italic tracking-tighter px-4">You're All Set!</h3>
    <p className="text-slate-400 max-w-sm mt-3 text-[10px] sm:text-xs font-medium italic leading-relaxed uppercase tracking-tight px-4">Your profile has been synchronized. The job agent is ready for use.</p>
    
    <div className="mt-8 sm:mt-10 md:mt-12 p-6 sm:p-7 md:p-8 bg-slate-50 border border-slate-100 rounded-[2rem] sm:rounded-[2.5rem] w-full max-w-sm mx-4 sm:mx-0">
       <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] sm:text-[10px] font-black text-slate-400 uppercase tracking-widest italic">Extension Status</span>
          <div className="flex items-center gap-2 px-2.5 sm:px-3 py-1 bg-white rounded-full border border-slate-200">
             <div className={`w-1.5 h-1.5 rounded-full ${extensionStatus.available ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
             <span className={`text-[8px] sm:text-[9px] font-black uppercase tracking-tight ${extensionStatus.available ? 'text-emerald-600' : 'text-red-600'}`}>
                {extensionStatus.available ? 'Connected' : 'Missing'}
             </span>
          </div>
       </div>
    </div>
  </div>
);

const SettingsWizard = ({ onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [extensionStatus, setExtensionStatus] = useState({ available: false, synced: false });
  const [tlError, setTlError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  
  // Step 1: Personal Info
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: ''
  });
  
  // Step 2: Resume Management
  const [resumes, setResumes] = useState([]);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeTitle, setResumeTitle] = useState('');
  
  // Step 3: Form Control
  const [inputFieldConfigs, setInputFieldConfigs] = useState([]);
  const [radioButtons, setRadioButtons] = useState([]);
  const [dropdowns, setDropdowns] = useState([]);
  const [newInputPlaceholder, setNewInputPlaceholder] = useState('');
  const [newInputValue, setNewInputValue] = useState('');
  const [newRadioPlaceholder, setNewRadioPlaceholder] = useState('');
  const [newRadioValue, setNewRadioValue] = useState('');
  const [newDropdownPlaceholder, setNewDropdownPlaceholder] = useState('');
  const [newDropdownValue, setNewDropdownValue] = useState('');
  
  // Step 4: Filter Settings
  const [titleSkipEnabled, setTitleSkipEnabled] = useState(false);
  const [titleFilterEnabled, setTitleFilterEnabled] = useState(false);
  const [badWordsEnabled, setBadWordsEnabled] = useState(false);
  const [titleSkipWords, setTitleSkipWords] = useState([]);
  const [titleFilterWords, setTitleFilterWords] = useState([]);
  const [badWords, setBadWords] = useState([]);
  const [newTitleSkip, setNewTitleSkip] = useState('');
  const [newTitleFilter, setNewTitleFilter] = useState('');
  const [newBadWord, setNewBadWord] = useState('');

  // Real-time progress calculation
  const calculateProgress = () => {
    let completed = 0;
    const totalSteps = 4;
    
    // 1. Personal Info (At least first/last name)
    if (personalInfo?.firstName && personalInfo?.lastName) completed++;
    
    // 2. Resume (At least one resume exists)
    if (resumes?.length > 0) completed++;
    
    // 3. Form Auto-fill (At least one mapping)
    if (inputFieldConfigs?.length > 0 || radioButtons?.length > 0) completed++;
    
    // 4. Filters (At least one word or enabled)
    if (titleSkipWords?.length > 0 || titleFilterWords?.length > 0 || badWords?.length > 0 || titleSkipEnabled || titleFilterEnabled || badWordsEnabled) completed++;
    
    return Math.round((completed / totalSteps) * 100);
  };

  const progress = calculateProgress();
  
  // Fixed: Reverted titles to standard "User Friendly" names while keeping icons/design
  const steps = [
    { id: 'personal-info', title: 'Personal Info', description: 'Your basic details', icon: User, component: PersonalInfoStep },
    { id: 'resume', title: 'Resume', description: 'Upload your CV', icon: FileText, component: ResumeStep },
    { id: 'form-control', title: 'Form Auto-fill', description: 'Custom field mapping', icon: Sliders, component: FormControlStep },
    { id: 'filters', title: 'Job Filters', description: 'Targeting rules', icon: Filter, component: FiltersStep },
    { id: 'complete', title: 'Finish', description: 'Ready to launch', icon: Zap, component: CompleteStep }
  ];

  useEffect(() => {
    checkStatus();
    loadUserData();
    loadResumes();
    loadFormControlData();
    loadFilterSettings();
  }, []);

  useEffect(() => {
    if (currentStep === 1) loadResumes();
    if (currentStep === 2) loadFormControlData();
    if (currentStep === 3) loadFilterSettings();
  }, [currentStep]);

  const checkStatus = async () => {
    const status = await checkExtensionStatus();
    setExtensionStatus(status);
  };

  const loadUserData = async () => {
    try {
      const response = await axiosInstance.get('/user');
      const user = response.data;
      setPersonalInfo({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        location: user.location || ''
      });

      // Initialize filters from backend settings if available
      if (user.settings) {
        setTitleSkipEnabled(user.settings.titleSkipEnabled || false);
        setTitleFilterEnabled(user.settings.titleFilterEnabled || false);
        setBadWordsEnabled(user.settings.badWordsEnabled || false);
        setTitleSkipWords(user.settings.titleSkipWords || []);
        setTitleFilterWords(user.settings.titleFilterWords || []);
        setBadWords(user.settings.badWords || []);
      }
    } catch (e) {}
  };

  const loadResumes = async () => {
    setLoadingResumes(true);
    try {
      const response = await axiosInstance.get('/resumes');
      setResumes(response.data.items || []);
    } catch (e) {} finally {
      setLoadingResumes(false);
    }
  };

  const handleActivateResume = async (resumeId) => {
    setTlError("");
    try {
      console.log("Attempting to activate resume:", resumeId);
      const resp = await axiosInstance.post('/resumes/activate', { resume_id: resumeId });
      console.log("Activation response:", resp.data);
      if (resp.data.ok) {
        await loadResumes();
      } else {
        setTlError("Activation reject by uplink.");
      }
    } catch (e) {
      console.error("Neural link failure during activation:", e);
      setTlError("Neural link failure during activation.");
    }
  };

  const loadFormControlData = async () => {
    try {
      const configs = await getFormControlConfigs();
      setInputFieldConfigs(configs.inputFieldConfigs || []);
      setRadioButtons(configs.radioButtons || []);
      setDropdowns(configs.dropdowns || []);
    } catch (e) {}
  };

  const loadFilterSettings = async () => {
    try {
      if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
        chrome.runtime.sendMessage({ action: 'getFilterSettings' }, (response) => {
          if (response && !chrome.runtime.lastError) {
            setTitleSkipEnabled(response.titleSkipEnabled || false);
            setTitleFilterEnabled(response.titleFilterEnabled || false);
            setBadWordsEnabled(response.badWordsEnabled || false);
            setTitleSkipWords(response.titleSkipWords || []);
            setTitleFilterWords(response.titleFilterWords || []);
            setBadWords(response.badWords || []);
          }
        });
      }
    } catch (e) {}
  };

  const handleGlobalSave = async () => {
    setSaving(true);
    setTlError("");
    try {
      await saveStepData(currentStep);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (e) {
      setTlError("Satellite link failure. Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setSaving(true);
      try {
        await saveStepData(currentStep);
        setCurrentStep(currentStep + 1);
      } catch (e) {
        setTlError("Failed to transition. Check your connection.");
      } finally {
        setSaving(false);
      }
    } else {
      await handleComplete();
    }
  };

  const handleBack = () => { if (currentStep > 0) setCurrentStep(currentStep - 1); };

  const saveStepData = async (stepIndex) => {
    const step = steps[stepIndex];
    if (step.id === 'personal-info') {
      try {
        const formData = new FormData();
        formData.append('first_name', personalInfo.firstName);
        formData.append('last_name', personalInfo.lastName);
        formData.append('phone', personalInfo.phone);
        formData.append('location', personalInfo.location);
        await axiosInstance.patch('/user', formData);
        const configs = await getFormControlConfigs();
        await setFormControlConfigs({ ...configs, defaultFields: { ...personalInfo } });
      } catch (e) {}
    } else if (step.id === 'resume') {
      if (resumeFile && resumeTitle) {
        try {
          const formData = new FormData();
          formData.append('resume', resumeFile);
          formData.append('title', resumeTitle);
          formData.append('make_active', 'true');
          await axiosInstance.post('/resumes/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
          await loadResumes();
        } catch (e) {}
      }
    } else if (step.id === 'form-control') {
      try {
        const configs = await getFormControlConfigs();
        await setFormControlConfigs({ ...configs, inputFieldConfigs, radioButtons, dropdowns });
      } catch (e) {}
    } else if (step.id === 'filters') {
      try {
        const filterData = { titleSkipEnabled, titleFilterEnabled, badWordsEnabled, titleSkipWords, titleFilterWords, badWords };
        
        // Save to Chrome Extension
        if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
          chrome.runtime.sendMessage({
            action: 'setFilterSettings',
            data: filterData
          });
        }

        // Save to Backend
        const formData = new FormData();
        formData.append('settings', JSON.stringify(filterData));
        await axiosInstance.patch('/user', formData);
      } catch (e) {}
    }
  };

  const handleComplete = async () => {
    try {
      await saveStepData(currentStep);
      localStorage.setItem('applyease_wizard_completed', 'true');
      if (onComplete) onComplete(); else navigate('/dashboard');
    } catch (error) {
      alert('Sync failure.');
    }
  };

  const CurrentStepComponent = steps[currentStep].component;

  return (
    <div className="w-full max-w-4xl mx-auto bg-white border border-slate-200 rounded-[2rem] md:rounded-[3rem] shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 font-sans flex flex-col max-h-[calc(100vh-1rem)] sm:max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)]">
      <div className="p-3 sm:p-4 md:p-5 lg:p-10 border-b border-slate-100 bg-slate-50/50 relative overflow-hidden flex-shrink-0">
        <div className="absolute top-0 right-0 p-8 md:p-10 opacity-5 pointer-events-none -rotate-12"><Cpu size={100} className="text-blue-600 md:w-[140px] md:h-[140px]" /></div>
        
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3 md:gap-4 relative z-10">
          <div className="flex-1 min-w-0 w-full sm:w-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
               <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-slate-900 tracking-tight italic flex items-center gap-1.5 sm:gap-2 md:gap-3">
                  <Rocket size={16} className="text-blue-600 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-8 lg:h-8 flex-shrink-0" /> 
                  <span className="truncate">Setup Assistant</span>
               </h2>
               <div className="flex flex-col flex-1 sm:max-w-[180px] md:max-w-[200px] mt-0.5 sm:mt-1 sm:ml-3 md:ml-4 w-full sm:w-auto">
                  <div className="flex items-center justify-between mb-0.5 sm:mb-1">
                     <span className="text-[7px] sm:text-[8px] font-black text-slate-400 uppercase tracking-widest italic">{progress}% Complete</span>
                  </div>
                  <div className="h-1 sm:h-1.5 w-full bg-slate-200 rounded-full overflow-hidden shadow-inner">
                     <div 
                        className="h-full bg-blue-600 rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${progress}%` }}
                     />
                  </div>
               </div>
            </div>
            <p className="text-slate-500 text-[9px] sm:text-[10px] md:text-xs font-black uppercase tracking-widest mt-0.5 sm:mt-1 truncate">Configure your Job Agent Profile</p>
            {tlError && <p className="text-rose-500 text-[8px] sm:text-[9px] md:text-[10px] font-black uppercase mt-1 sm:mt-2 animate-pulse italic break-words">{tlError}</p>}
          </div>

          <div className="flex flex-row items-center gap-1.5 sm:gap-2 md:gap-3">
             <button
               onClick={handleGlobalSave}
               disabled={saving}
               className={`flex items-center justify-center gap-1 px-2.5 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-3 rounded-xl sm:rounded-2xl text-[8px] sm:text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all italic shadow-lg active:scale-95 whitespace-nowrap focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 ${
                 saveSuccess 
                   ? 'bg-emerald-500 text-white shadow-emerald-500/20' 
                   : 'bg-slate-900 text-white hover:bg-black shadow-slate-900/20'
               }`}
             >
               {saving ? <RefreshCw size={10} className="sm:w-3 sm:h-3 md:w-[14px] md:h-[14px] animate-spin" /> : saveSuccess ? <Check size={10} className="sm:w-3 sm:h-3 md:w-[14px] md:h-[14px]" /> : <Rocket size={10} className="sm:w-3 sm:h-3 md:w-[14px] md:h-[14px]" />}
               <span className="truncate hidden sm:inline">{saving ? 'Syncing...' : saveSuccess ? 'Saved' : 'Save Config'}</span>
               <span className="truncate sm:hidden">{saving ? 'Sync' : saveSuccess ? '✓' : 'Save'}</span>
             </button>

             <button
               onClick={() => { if (window.confirm('Exit setup assistant?')) onComplete ? onComplete() : navigate('/settings'); }}
               className="px-2.5 py-1.5 sm:px-4 sm:py-2 md:px-6 md:py-3 text-[8px] sm:text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-900 transition-all border border-slate-200 hover:border-slate-400 rounded-xl sm:rounded-2xl italic whitespace-nowrap focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1"
             >
               <span className="hidden sm:inline">Skip Setup</span>
               <span className="sm:hidden">Skip</span>
             </button>
          </div>
        </div>

        <div className="flex overflow-x-auto thin-scrollbar md:justify-between items-center mt-4 sm:mt-6 md:mt-12 px-0.5 sm:px-1 md:px-4 relative z-10 gap-2 sm:gap-3 md:gap-0 pb-1 sm:pb-2">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            return (
              <button
                key={step.id}
                onClick={() => setCurrentStep(index)}
                className="flex flex-col items-center group relative flex-1 min-w-[60px] sm:min-w-[70px] md:min-w-0 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 rounded-lg sm:rounded-xl p-1 sm:p-1.5 md:p-2 transition-all"
                aria-label={`Go to ${step.title} step`}
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-xl sm:rounded-[1.2rem] md:rounded-[1.5rem] flex items-center justify-center transition-all duration-500 border-2 relative z-10 ${
                  isActive ? 'bg-blue-600 border-blue-600 text-white shadow-xl sm:shadow-2xl shadow-blue-500/20 scale-105 sm:scale-110' :
                  isCompleted ? 'bg-emerald-500 border-emerald-500 text-white hover:scale-105' :
                  'bg-white border-slate-200 text-slate-300 hover:border-slate-300 hover:scale-105'
                }`}>
                  {isCompleted ? <Check size={16} className="sm:w-5 sm:h-5 md:w-6 md:h-6 stroke-[3]" /> : <StepIcon size={16} className={`sm:w-5 sm:h-5 md:w-6 md:h-6 ${isActive ? 'animate-pulse' : ''}`} />}
                </div>
                <div className="mt-1.5 sm:mt-2 md:mt-3 text-center">
                   <p className={`text-[7px] sm:text-[8px] md:text-[9px] font-black uppercase tracking-widest ${isActive ? 'text-blue-600' : isCompleted ? 'text-emerald-500' : 'text-slate-400'}`}>
                      {step.title}
                   </p>
                </div>
                {index < steps.length - 1 && (
                   <div className={`absolute top-5 sm:top-6 md:top-7 left-1/2 w-full h-[2px] sm:h-[2.5px] md:h-[3px] transition-all duration-1000 -z-0 ${isCompleted ? 'bg-emerald-500' : 'bg-slate-100'}`} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-5 md:p-12 min-h-[300px] sm:min-h-[450px] overflow-y-auto thin-scrollbar flex-1">
        <CurrentStepComponent
          personalInfo={personalInfo} setPersonalInfo={setPersonalInfo}
          resumeFile={resumeFile} setResumeFile={setResumeFile}
          resumeTitle={resumeTitle} setResumeTitle={setResumeTitle}
          inputFieldConfigs={inputFieldConfigs} setInputFieldConfigs={setInputFieldConfigs}
          radioButtons={radioButtons} setRadioButtons={setRadioButtons}
          dropdowns={dropdowns} setDropdowns={setDropdowns}
          newInputPlaceholder={newInputPlaceholder} setNewInputPlaceholder={setNewInputPlaceholder}
          newInputValue={newInputValue} setNewInputValue={setNewInputValue}
          newRadioPlaceholder={newRadioPlaceholder} setNewRadioPlaceholder={setNewRadioPlaceholder}
          newRadioValue={newRadioValue} setNewRadioValue={setNewRadioValue}
          newDropdownPlaceholder={newDropdownPlaceholder} setNewDropdownPlaceholder={setNewDropdownPlaceholder}
          newDropdownValue={newDropdownValue} setNewDropdownValue={setNewDropdownValue}
          titleSkipEnabled={titleSkipEnabled} setTitleSkipEnabled={setTitleSkipEnabled}
          titleFilterEnabled={titleFilterEnabled} setTitleFilterEnabled={setTitleFilterEnabled}
          badWordsEnabled={badWordsEnabled} setBadWordsEnabled={setBadWordsEnabled}
          titleSkipWords={titleSkipWords} setTitleSkipWords={setTitleSkipWords}
           titleFilterWords={titleFilterWords} setTitleFilterWords={setTitleFilterWords}
           badWords={badWords} setBadWords={setBadWords}
           newTitleSkip={newTitleSkip} setNewTitleSkip={setNewTitleSkip}
           newTitleFilter={newTitleFilter} setNewTitleFilter={setNewTitleFilter}
           newBadWord={newBadWord} setNewBadWord={setNewBadWord}
           extensionStatus={extensionStatus} navigate={navigate}
           resumes={resumes} loadingResumes={loadingResumes}
           handleActivateResume={handleActivateResume}
        />
      </div>

      <div className="px-3 py-3 sm:px-4 sm:py-4 md:px-12 md:py-8 bg-slate-50 border-t border-slate-100 flex flex-row items-center justify-between gap-2 sm:gap-4 flex-shrink-0">
        <button 
           className={`px-3 py-2 sm:px-5 sm:py-2.5 md:px-8 md:py-3 rounded-xl sm:rounded-2xl text-[8px] sm:text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all italic flex-shrink-0 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 ${currentStep > 0 ? 'bg-white border-2 border-slate-200 text-slate-500 hover:border-slate-400 active:scale-95' : 'opacity-0 pointer-events-none'}`} 
           onClick={handleBack}
        >
          <span className="hidden sm:inline">Previous</span>
          <span className="sm:hidden">Prev</span>
        </button>
        <div className="flex items-center justify-end gap-2 sm:gap-4 md:gap-6 flex-1">
           <div className="flex flex-col text-right hidden md:block">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Step</span>
              <span className="text-xs font-black text-slate-900 italic leading-none">{currentStep + 1} / {steps.length}</span>
           </div>
           <button 
              className="flex items-center justify-center gap-1.5 sm:gap-2 md:gap-3 px-4 py-2 sm:px-6 sm:py-2.5 md:px-10 md:py-4 bg-blue-600 text-white rounded-xl sm:rounded-[1.2rem] md:rounded-[1.5rem] text-[8px] sm:text-[9px] md:text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 active:scale-95 transition-all shadow-xl sm:shadow-2xl shadow-blue-500/20 italic flex-1 sm:flex-initial min-w-[100px] sm:min-w-[120px] md:min-w-0 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1" 
              onClick={handleNext}
           >
             <span className="truncate">{currentStep === steps.length - 1 ? 'Finish' : 'Continue'}</span>
             <ChevronRight size={12} className="sm:w-4 sm:h-4 md:w-[18px] md:h-[18px] flex-shrink-0" />
           </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsWizard;
